import type { CheerioAPI } from "cheerio";
import type { Url, AdapterId } from "../../data-model/primitives.js";
import type { NovelMetadata, NovelStatus } from "../../data-model/novel.js";
import { ParseError } from "../../core/errors.js";
import { extractCoverUrl } from "./cover.js";

export interface NovelHeaderSelectors {
  readonly title: string;
  readonly author: string;
  /** Single selector, or ordered fallbacks — the first non-empty match wins. */
  readonly description: string | readonly string[];
  /** Anchor selector iterated with `.each()`; results are deduped. */
  readonly genres: string;
  readonly cover: string;
}

export interface NovelHeader {
  readonly title: string;
  readonly author: string;
  readonly description: string;
  readonly genres: readonly string[];
  readonly coverUrl?: Url;
}

/** Parse the fields shared verbatim across the truyenfull and metruyenchu novel pages. */
export function parseNovelHeader(
  $: CheerioAPI,
  selectors: NovelHeaderSelectors,
  sourceUrl: Url,
): NovelHeader {
  const title = $(selectors.title).first().text().trim();
  if (!title) throw new ParseError("Missing title on novel page", { url: sourceUrl });

  const author = $(selectors.author).first().text().trim();

  const descSelectors =
    typeof selectors.description === "string" ? [selectors.description] : selectors.description;
  let description = "";
  for (const sel of descSelectors) {
    const t = $(sel).first().text().trim();
    if (t) {
      description = t;
      break;
    }
  }
  description = description.replace(/\s+/g, " ").trim();

  const genres: string[] = [];
  $(selectors.genres).each((_, el) => {
    const t = $(el).text().trim();
    if (t && !genres.includes(t)) genres.push(t);
  });

  const coverUrl = extractCoverUrl($, selectors.cover, sourceUrl);

  return {
    title,
    author,
    description,
    genres,
    ...(coverUrl !== undefined ? { coverUrl } : {}),
  };
}

export interface NovelEnvelope {
  readonly sourceUrl: Url;
  readonly sourceSite: AdapterId;
  readonly status: NovelStatus;
  readonly totalChapters?: number | undefined;
}

/**
 * Assembles the NovelMetadata envelope: stamps fetchedAt (the single
 * non-determinism seam in parsing) and owns the omit-when-undefined spreads.
 */
export function buildNovelMetadata(fields: NovelHeader, envelope: NovelEnvelope): NovelMetadata {
  return {
    title: fields.title,
    author: fields.author,
    description: fields.description,
    genres: fields.genres,
    sourceUrl: envelope.sourceUrl,
    sourceSite: envelope.sourceSite,
    status: envelope.status,
    fetchedAt: new Date(),
    ...(fields.coverUrl !== undefined ? { coverUrl: fields.coverUrl } : {}),
    ...(envelope.totalChapters !== undefined ? { totalChapters: envelope.totalChapters } : {}),
  };
}
